import { ExpandedState, SortingState } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPastFlightsForCampaign } from 'src/components/SponsoredListings/SponsoredListings.util';
import {
  CampaignSalesAggregate,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext/useCatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { NoData } from 'src/tables/Table';
import { GroupedVirtuosoTable } from 'src/tables/TableV2/GroupedVirtuosoTable';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getEventPerformerVenue,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import {
  Feature,
  Permission,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

import { CampaignRowForm } from '../CampaignRowForm';
import * as styles from '../SponsoredListingsTable.css';
import {
  CampaignsTableRow,
  getTableColumnDefinitions,
} from './CampaignsTableV2ColumnsConfig';
import { useCampaignsTableMetricsData } from './useCampaignsTableMetricsData';

export type CampaignsTableV2Props = {
  isLoading: boolean;
  disabled?: boolean;
  campaigns: SponsoredListingsCampaign[] | null | undefined;
  onStartWork: () => void;
  onStopWork: (success: boolean) => void;
  showEventColumns?: boolean;
};

export const CampaignsTableV2: React.FC<CampaignsTableV2Props> = ({
  isLoading,
  disabled,
  campaigns,
  onStartWork,
  onStopWork,
  showEventColumns,
}) => {
  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const { data } = useCatalogDataContext();
  const { getUiCurrency } = useLocalizationContext();
  const { campaignSalesAggregates } = useAdCampaignsDataContext();
  const hasReportingMetrics = useUserHasFeature(
    Feature.SponsoredListingsV2_ReportingMetrics
  );

  // Fetch and merge reporting metrics with campaigns data
  const campaignsWithMetrics = useCampaignsTableMetricsData(campaigns);

  const columns = useMemo(() => {
    let columns = getTableColumnDefinitions(onStartWork, onStopWork, disabled);
    if (!showEventColumns) {
      columns = columns.filter(
        (column) => column.id !== 'eventName' && column.id !== 'eventDate'
      );
    }
    if (!hasReportingMetrics) {
      columns = columns.filter(
        (column) =>
          column.id !== 'historic-potential-impressions' &&
          column.id !== 'actual-impressions' &&
          column.id !== 'impression-share' &&
          column.id !== 'clicks' &&
          column.id !== 'potential-sales' &&
          column.id !== 'potential-revenue'
      );
    }
    return columns;
  }, [
    onStartWork,
    onStopWork,
    disabled,
    showEventColumns,
    hasReportingMetrics,
  ]);

  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [sortState, setSortState] = useState<SortingState>([]);

  const contentContext = useContentContext();
  const olderFlightsText = useContent(ContentId.OlderFlights);

  const flightText = useCallback(
    (flight: SponsoredListingsFlight, isPrevious: boolean) => {
      const startDate = new Date(flight.dateRange.start!);
      const endDate = new Date(flight.dateRange.end!);
      const start = formatInTimeZone(startDate, 'UTC', 'M/d/yy');

      // Calculate duration in hours
      const durationHours =
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

      if (durationHours >= 0 && durationHours < 25) {
        // Daily (23-25 hours)
        return flight.isCurrent
          ? getContent(ContentId.Today, contentContext)
          : isPrevious
            ? getContent(ContentId.Yesterday, contentContext)
            : start;
      } else if (durationHours >= 6 * 24 && durationHours < 8 * 24) {
        // Weekly (6-8 days)
        return `${start} + 7`;
      } else if (durationHours > 27 * 24 && durationHours < 32 * 24) {
        // Monthly (27-32 days)
        return `${start} + 28`;
      } else {
        // Other duration - show date range
        return `${start} - ${formatInTimeZone(endDate, 'UTC', 'M/d/yy')}`;
      }
    },
    [contentContext]
  );

  const getSubRows = useCallback(
    (data: CampaignsTableRow) => {
      if (data.campaign) {
        const rows = [];

        if (data.campaign.activeFlight) {
          rows.push({
            id: data.id + '-active',
            campaignId: data.campaignId,
            name: flightText(data.campaign.activeFlight, false),
            hasChildren: false,
            flight: data.campaign.activeFlight,
            eventId: data.campaign.eventId,
            currency: data.currency,
            salesData: data.salesData,
            biddingMode: data.biddingMode,
            flightDuration: data.flightDuration,
          } as CampaignsTableRow);
        }

        const pastFlights = getPastFlightsForCampaign(data.campaign);
        if (pastFlights && pastFlights.length > 0) {
          const prevFlight = pastFlights[0];
          rows.push({
            id: data.id + '-previous',
            campaignId: data.campaignId,
            name: flightText(prevFlight, true),
            hasChildren: false,
            flight: prevFlight,
            currency: data.currency,
            salesData: data.salesData,
            biddingMode: data.biddingMode,
            flightDuration: data.flightDuration,
          } as CampaignsTableRow);

          if (pastFlights.length > 1) {
            rows.push({
              id: data.id + '-passed',
              campaignId: data.campaignId,
              name: `${olderFlightsText} (${pastFlights.length})`,
              hasChildren: true,
              flights: pastFlights.slice(1),
              currency: data.currency,
              salesData: data.salesData,
              biddingMode: data.campaign.biddingMode,
              flightDuration: data.flightDuration,
            } as CampaignsTableRow);
          }
        }
        return rows;
      }

      if (data.flights && data.flights.length > 0) {
        return data.flights.map(
          (flight, index) =>
            ({
              id: flight.flightId.toString(),
              campaignId: data.campaignId,
              name: `${flightText(flight, false)}`,
              hasChildren: false,
              flight: flight,
              currency: data.currency,
              salesData: data.salesData,
              biddingMode: data.biddingMode,
              flightDuration: data.flightDuration,
            }) as CampaignsTableRow
        );
      }

      return [];
    },
    [flightText, olderFlightsText]
  );

  const tableData = useMemo<CampaignsTableRow[]>(
    () =>
      (campaignsWithMetrics ?? [])
        .toSorted((a, b) => {
          // Sort by campaign date desc

          const aDate = a.dateRange.start
            ? new Date(a.dateRange.start).getTime()
            : 0;
          const bDate = b.dateRange.start
            ? new Date(b.dateRange.start).getTime()
            : 0;

          return bDate - aDate;
        })
        .map((c) => {
          const { event, venue, performer } = getEventPerformerVenue(
            c.eventId.toString(),
            data
          );

          const [eventName, eventNameConnector, eventSubName] =
            getFormattedEventName({
              event: event?.event,
              performer,
              venue,
              slimMode: false,
            });

          const salesData = campaignSalesAggregates?.[c.campaignId];
          const currency = getUiCurrency(
            salesData?.currencyCode || c.currencyCode || 'USD'
          );

          return {
            id: c.campaignId.toString(),
            campaignId: c.campaignId,
            name: c.campaignName,
            campaign: c,
            eventId: c.eventId,
            eventName: `${eventName}${eventNameConnector}${eventSubName}`,
            eventNameParts: [eventName, eventNameConnector, eventSubName],
            eventDate: event?.event?.dates.start
              ? new Date(event.event.dates.start)
              : undefined,
            currency: currency,
            salesData: salesData,
            biddingMode: c.biddingMode,
            flightDuration: c.flightDuration,
            hasChildren: Boolean(
              c.activeFlight ||
              Boolean(c.flights?.length) ||
              Boolean(getPastFlightsForCampaign(c)?.length)
            ),
          } as CampaignsTableRow;
        }),
    [campaignSalesAggregates, campaignsWithMetrics, data, getUiCurrency]
  );

  const onRowWrapper = useCallback(
    (row: CampaignsTableRow, tableRow: React.ReactNode) => {
      const isParent = row.campaign != null || row.flights != null;
      return (
        <div
          className={isParent ? styles.parentRowWrapper : styles.rowWrapper}
          onClick={() =>
            setExpandedState((prev: ExpandedState) => {
              if (row.campaign != null || row.flights != null) {
                const record = prev as Record<string, boolean>;
                record[row.id.toString()] = !record[row.id.toString()];
                return { ...record };
              }
              return prev;
            })
          }
        >
          <CampaignRowForm campaign={row.campaign!}>{tableRow}</CampaignRowForm>
        </div>
      );
    },
    [setExpandedState]
  );

  useEffect(() => {
    if (tableData.length > 0 && Object.keys(expandedState).length === 0) {
      setExpandedState({ [tableData[0].id]: true });
    }
  }, [tableData, expandedState]);

  const tableMeta = useMemo(() => {
    const campaignSums = campaignsWithMetrics?.reduce(
      (sums, c) => {
        const salesData = campaignSalesAggregates?.[c.campaignId];

        sums.currencyCode = c.currencyCode || sums.currencyCode;
        sums.totalBudget += c.totalBudget ?? 0;
        sums.totalSpend += c.totalSpend ?? 0;
        sums.totalSales += c.salesCount ?? salesData?.saleCount ?? 0;
        sums.totalCost += salesData?.totalCost ?? 0;
        sums.totalNetProceeds +=
          c.salesProceeds ?? salesData?.totalNetProceeds ?? 0;
        sums.totalHistoricPotentialImpressions +=
          c.historicPotentialImpressions ?? 0;
        sums.totalActualImpressions += c.actualImpressions ?? 0;
        sums.totalClicks += c.clicks ?? 0;
        sums.totalSalesCount += c.salesCount ?? 0;
        sums.totalSalesProceeds += c.salesProceeds ?? 0;
        sums.totalPotentialSales += c.potentialSales ?? 0;
        sums.totalPotentialRevenue += c.potentialRevenue ?? 0;

        sums.activeFlight.budget += c.activeFlight?.flightBudget ?? 0;
        sums.activeFlight.spend += c.activeFlight?.flightSpend ?? 0;

        const activeAggregate =
          salesData?.flightsAggregate?.[c.activeFlight?.flightId ?? 0];
        sums.activeFlight.netProceeds +=
          c.activeFlight?.salesProceeds ??
          activeAggregate?.totalNetProceeds ??
          0;
        sums.activeFlight.salesCount +=
          c.activeFlight?.salesCount ?? activeAggregate?.saleCount ?? 0;
        sums.activeFlight.salesCountMetrics += c.activeFlight?.salesCount ?? 0;
        sums.activeFlight.salesProceedsMetrics +=
          c.activeFlight?.salesProceeds ?? 0;
        sums.activeFlight.potentialSales += c.activeFlight?.potentialSales ?? 0;
        sums.activeFlight.potentialRevenue +=
          c.activeFlight?.potentialRevenue ?? 0;

        const pastFlights = getPastFlightsForCampaign(c);
        if (pastFlights && pastFlights.length > 0) {
          const prevFlight = pastFlights[0];
          sums.previousFlight.budget += prevFlight.flightBudget ?? 0;
          sums.previousFlight.spend += prevFlight.flightSpend ?? 0;
          const previousAggregate =
            salesData?.flightsAggregate?.[prevFlight.flightId ?? 0];
          sums.previousFlight.netProceeds +=
            prevFlight.salesProceeds ??
            previousAggregate?.totalNetProceeds ??
            0;
          sums.previousFlight.salesCount +=
            prevFlight.salesCount ?? previousAggregate?.saleCount ?? 0;
          sums.previousFlight.salesCountMetrics += prevFlight.salesCount ?? 0;
          sums.previousFlight.salesProceedsMetrics +=
            prevFlight.salesProceeds ?? 0;
          sums.previousFlight.potentialSales += prevFlight.potentialSales ?? 0;
          sums.previousFlight.potentialRevenue +=
            prevFlight.potentialRevenue ?? 0;
        }

        return sums;
      },
      {
        currencyCode: 'USD',
        totalBudget: 0,
        totalSpend: 0,
        totalSales: 0,
        totalCost: 0,
        totalNetProceeds: 0,
        totalHistoricPotentialImpressions: 0,
        totalActualImpressions: 0,
        totalClicks: 0,
        totalSalesCount: 0,
        totalSalesProceeds: 0,
        totalPotentialSales: 0,
        totalPotentialRevenue: 0,
        activeFlight: {
          budget: 0,
          spend: 0,
          netProceeds: 0,
          salesCount: 0,
          salesCountMetrics: 0,
          salesProceedsMetrics: 0,
          potentialSales: 0,
          potentialRevenue: 0,
        },
        previousFlight: {
          budget: 0,
          spend: 0,
          netProceeds: 0,
          salesCount: 0,
          salesCountMetrics: 0,
          salesProceedsMetrics: 0,
          potentialSales: 0,
          potentialRevenue: 0,
        },
      }
    );

    const currency = getUiCurrency(campaignSums?.currencyCode || 'USD');

    const totalHistoricPotentialImpressions =
      campaignSums?.totalHistoricPotentialImpressions ?? 0;
    const totalActualImpressions = campaignSums?.totalActualImpressions ?? 0;
    const totalImpressionShare =
      totalHistoricPotentialImpressions > 0
        ? (totalActualImpressions / totalHistoricPotentialImpressions) * 100
        : null;

    return {
      summaryData: {
        id: 'summary-total',
        campaignId: 0,
        name: 'Total',
        currency: currency,
        campaign: {
          campaignId: 0,
          campaignName: 'Total',
          totalBudget: campaignSums?.totalBudget,
          totalSpend: campaignSums?.totalSpend,
          currencyCode: campaignSums?.currencyCode || 'USD',
          historicPotentialImpressions: totalHistoricPotentialImpressions,
          actualImpressions: totalActualImpressions,
          impressionShare: totalImpressionShare,
          clicks: campaignSums?.totalClicks ?? 0,
          salesCount: campaignSums?.totalSalesCount ?? 0,
          salesProceeds: campaignSums?.totalSalesProceeds ?? 0,
          potentialSales: campaignSums?.totalPotentialSales ?? 0,
          potentialRevenue: campaignSums?.totalPotentialRevenue ?? 0,
        },
        flight: {
          flightId: 0,
          flightBudget: campaignSums?.activeFlight.budget,
          flightSpend: campaignSums?.activeFlight.spend,
          salesCount: campaignSums?.activeFlight.salesCountMetrics ?? 0,
          salesProceeds: campaignSums?.activeFlight.salesProceedsMetrics ?? 0,
          potentialSales: campaignSums?.activeFlight.potentialSales ?? 0,
          potentialRevenue: campaignSums?.activeFlight.potentialRevenue ?? 0,
        },
        flights: [
          {
            flightId: 1,
            flightBudget: campaignSums?.previousFlight.budget,
            flightSpend: campaignSums?.previousFlight.spend,
            salesCount: campaignSums?.previousFlight.salesCountMetrics ?? 0,
            salesProceeds:
              campaignSums?.previousFlight.salesProceedsMetrics ?? 0,
            potentialSales: campaignSums?.previousFlight.potentialSales ?? 0,
            potentialRevenue:
              campaignSums?.previousFlight.potentialRevenue ?? 0,
          },
        ],
        salesData: {
          totalCost: campaignSums?.totalCost,
          totalNetProceeds: campaignSums?.totalNetProceeds,
          saleCount: campaignSums?.totalSales,
          currencyCode: campaignSums?.currencyCode || 'USD',
          flightsAggregate: {
            0: {
              totalNetProceeds: campaignSums?.activeFlight.netProceeds,
              totalCost: 0,
              saleCount: campaignSums?.activeFlight.salesCount,
              currencyCode: campaignSums?.currencyCode || 'USD',
            },
            1: {
              totalNetProceeds: campaignSums?.previousFlight.netProceeds,
              totalCost: 0,
              saleCount: campaignSums?.previousFlight.salesCount,
              currencyCode: campaignSums?.currencyCode || 'USD',
            },
          },
        } as CampaignSalesAggregate,
      } as CampaignsTableRow,
    };
  }, [campaignSalesAggregates, campaignsWithMetrics, getUiCurrency]);

  if (!hasAdPlatformViewPermission) {
    return null;
  }

  if (isLoading) {
    return <PosSpinner />;
  }

  return campaigns?.length ? (
    <ColumnResizingContextProvider>
      <GroupedVirtuosoTable<CampaignsTableRow>
        style={{ height: '100%' }}
        actionColId={'actions'}
        data={tableData}
        columns={columns}
        onExpandedChange={setExpandedState}
        onSortingChange={setSortState}
        getRowId={(data) => String(data.id)}
        getGroupId={(data) => data.campaignId.toString()}
        getSubRows={getSubRows}
        getItemId={(data) => data.id}
        renderGroupAsNormalRow
        isGroupRow={(data) => data.hasChildren}
        rowWrapper={onRowWrapper}
        state={{
          sorting: sortState,
          expanded: expandedState,
        }}
        meta={tableMeta}
      />
    </ColumnResizingContextProvider>
  ) : (
    <NoData style={{ border: `1px solid ${vars.color.borderPrimary}` }}>
      <Content id={ContentId.None} />
    </NoData>
  );
};
